import { Person, Search } from '@tunasong/icons'
import { Add, Favorite, Home, Tool } from '@tunasong/icons'
import { usePlugins } from '@tunasong/plugin-lib'
import type { Entity, EntityType, Persisted } from '@tunasong/schemas'
import { useSearchDialog } from '@tunasong/search'
import { useLocation, useNavigate } from '@tunasong/ui-lib'
import { useCallback, useState } from 'react'
import CreateEntityDialog from '../entity/create-entity-dialog.js'
import ToolsDialog from './tools-dialog.js'
import styles from './mobile-navigation.module.css'

interface MobileNavigationProps {
  /** Name, icon of the views */
  entity?: Persisted<Entity>
  createNew?: EntityType[] | boolean
}
export function MobileNavigation(props: MobileNavigationProps) {
  const { entity, createNew = false } = props

  const location = useLocation()
  const isHome = location.pathname === '/app'
  /** Automations has no tab of its own on phone; it is reached from Profile, so Profile stays lit there */
  const isProfile = location.pathname === '/app/profile' || location.pathname === '/app/automations'
  const isFavorities = location.pathname === '/app/favorites'

  const { dialog: searchDialog, setOpen, open: searchOpen } = useSearchDialog()

  const currentAction = searchOpen
    ? 'Search'
    : isHome
      ? 'Home'
      : isProfile
        ? 'Profile'
        : isFavorities
          ? 'Favorities'
          : null

  const [dialogOpen, setDialogOpen] = useState(false)

  const plugins = usePlugins('all')

  const handleClose = useCallback(({}: { restoreLayout?: boolean } = {}) => {
    setDialogOpen(false)
  }, [])

  const navigate = useNavigate()
  const handleFavorites = () => navigate('/app/favorites')
  const [toolsDialogOpen, setToolsDialogOpen] = useState(false)

  if (!plugins) {
    return null
  }

  return (
    <>
      <div className={styles.container}>
        <nav className={styles.nav}>
          {[
            { key: 'home', value: 'Home', label: 'Home', icon: <Home />, onClick: () => navigate('/app') },
            ...(createNew && plugins
              ? [{ key: 'nav-new', value: 'New', label: 'New', icon: <Add />, onClick: () => setDialogOpen(true) }]
              : []),
            {
              key: 'nav-tools',
              value: 'Tools',
              label: 'Tools',
              icon: <Tool />,
              onClick: () => setToolsDialogOpen(true),
            },
            { key: 'search', value: 'Search', label: 'Search', icon: <Search />, onClick: () => setOpen(true) },
            {
              key: 'favorites',
              value: 'Favorities',
              label: 'Favorites',
              icon: <Favorite />,
              onClick: handleFavorites,
            },
            {
              key: 'profile',
              value: 'Profile',
              label: 'Profile',
              icon: <Person />,
              onClick: () => navigate('/app/profile'),
            },
          ].map(item => (
            <button
              key={item.key}
              onClick={item.onClick}
              className={styles.navButton}
              aria-current={currentAction === item.value ? 'page' : undefined}
              style={{
                // Inactive labels were action-active at 70% opacity (2.68:1). The active item is marked by
                // weight and aria-current as well as colour, so colour is not the only cue (WCAG 1.4.1).
                color:
                  currentAction === item.value ? 'var(--tuna-palette-primary-main)' : 'var(--tuna-content-secondary)',
                fontWeight: currentAction === item.value ? 600 : undefined,
              }}
            >
              {item.icon}
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        {entity ? (
          <CreateEntityDialog open={dialogOpen} onClose={handleClose} plugins={plugins} parent={entity} />
        ) : null}
        <ToolsDialog variant="subtitle1" open={toolsDialogOpen} onClose={() => setToolsDialogOpen(false)} />
        {searchDialog}
      </div>
    </>
  )
}
